module Client.AdminEditPage

open Fable.Core
open Fable.Import
open Elmish
open Fable.React
open Fable.React.Props
open Shared.Domain
open Style
open System
open Fable.Core.JsInterop
open Fetch
open Fable
open Shared
open Utils
open AdminShared
open PageLogic
open Client.Pages
open Shared.Domain

type TargetPanel =
    | Panel1
    | Panel2

type LinkEmbed =
    | InsideBlock
    | CardImage

type EditPageState =
    {
        Page: GenericPage;
        IdentifierWhenLoaded: string;
        WarnIdentifier: bool;
        Preview: GenericPage option;
        ShowingFilePicker: bool
        Files: (string * FileMeta list) list
        SelectedFile: FileMeta option
        LinkTarget: (Guid * Guid * LinkEmbed) option
    }

type BlockValue<'a> = Guid * Guid * 'a

type Msg =
    | FetchError of exn
    | LoadedPageList of PageMeta list
    | ReturnToPageList
    | PreviewPage
    | ClosePreview
    | PagePreviewRendered of GenericPage
    | LoadedPage of GenericPage
    | PageClicked of Slug * ClickAction
    | MainTitleChanged of string
    | MainIdentifierChanged of Slug
    | BlockTypeChanged of TextBlock
    | BlockContentTypeChanged of BlockContent BlockValue
    | AddToTextBlock of scopeId: Guid * TargetPanel
    | AddToPage
    | AddNewPage
    | SavePage
    | PageSaved
    | ConfirmDeletePage
    | ConfirmedPageDeletion
    | CancelDeletion
    | PageDeleted
    | IdentifierUniquenessChecked of bool
    | PageBackgroundColourChanged of string
    | BlockBackgroundColourChanged of string BlockValue
    | DefaultTextColourChanged of string
    | BlockTextColourChanged of string BlockValue
    | YouTubeLinkChanged of string BlockValue
    | YouTubeHeightChanged of string BlockValue
    | YouTubeWidthChanged of string BlockValue
    | YouTubeFullScreenChanged of bool BlockValue
    | DefaultFontSizeChanged of string
    | BlockFontSizeChanged of string BlockValue
    | BlockHeaderColourChanged of ColourAccent BlockValue
    | HideBlockHeader of scopeId: Guid * blockId: Guid
    | ShowBlockHeader of scopeId: Guid * blockId: Guid
    | BlockHeaderTextChanged of string BlockValue
    | DeleteBlock of scopeId: Guid * blockId: Guid
    | DeleteTextBlock of id: Guid
    | MoveTextBlock of id: Guid * Direction
    | MoveBlock of Direction BlockValue
    | BlockAlignmentChanged of Justification BlockValue
    | ChangeCardShape of scopeId: Guid * ImageShape
    | ShowFilePicker of scopeId: Guid * blockId: Guid * target: LinkEmbed
    | HideFilePicker
    | InsertFileLink of scopeId: Guid * blockId: Guid * image: bool * link: string
    | LoadedFiles of FileMeta list
    | SelectFile of FileMeta
    | InsertButtonClicked
    | CardUrlChanged of string BlockValue
    | CardAltChanged of string BlockValue
    | CardHoverTextChanged of string BlockValue
    | ToggleLandingPage
    | SetClaims of string


type AdminPageAction =
    | Listing of LoadState<unit, PageMeta list>
    | Editing of LoadState<Slug, EditPageState>

type Model = 
    { 
        User: JWT;
        Action: AdminPageAction;
        ShowConfirmDelete: bool;
    }

let getFilesCmd (jwt: JWT) imagesOnly =
    let tenant = TenantRef.get jwt.Account.Tenant
    let url = if imagesOnly then Urls.Images tenant else Urls.Files tenant
    FetchRequest.getWithToken url jwt.Token LoadedFiles FetchError

let previewPageCmd token (page: GenericPage) =
    let url = Urls.GetPreview
    FetchRequest.postWithToken url page token PagePreviewRendered FetchError

let deletePageCmd (jwt: JWT) id = 
    let url = Urls.PageById (TenantRef.get jwt.Account.Tenant) (Slug.get id)
    FetchRequest.deleteWithToken url () jwt.Token (fun _ -> PageDeleted) FetchError

let postSavePageCmd (jwt: JWT) (page: GenericPage) oldId = 
    let url = Urls.Pages (TenantRef.get jwt.Account.Tenant)
    FetchRequest.postWithToken 
        url
        { Content = page; IdentifierWhenLoaded = oldId }
        jwt.Token
        (fun _ -> PageSaved) 
        FetchError

let getAllPagesCmd (jwt: JWT) = 
    let url = Urls.Pages (TenantRef.get jwt.Account.Tenant)
    FetchRequest.getWithToken url jwt.Token LoadedPageList FetchError

let getPageCmd (jwt: JWT) id = 
    let url = Urls.PageById (TenantRef.get jwt.Account.Tenant) (Slug.get id)
    FetchRequest.getWithToken url jwt.Token LoadedPage FetchError

let getPageUniquenessCmd (jwt: JWT) id = 
    let url = Urls.PageExistsById (TenantRef.get jwt.Account.Tenant) (Slug.get id)
    FetchRequest.getWithToken url jwt.Token IdentifierUniquenessChecked FetchError

let init user = 
    {
        User = user 
        Action = Listing (Loading())
        ShowConfirmDelete = false
    },  getAllPagesCmd user

let renderContent scopeId blockId lbl dispatch =
    function
    | Markdown txt -> 
        formGroup [
            textarea [
                Id lbl
                Placeholder "Enter the markdown content."
                HTMLAttr.Rows 10
                Key (sprintf "%O" blockId)
                ClassName "form-control content-area"
                DefaultValue txt
                OnChange (fun ev -> 
                    dispatch (BlockContentTypeChanged(scopeId, blockId, (Markdown !!ev.target?value))))
            ] []
        ]
    | RawText txt -> 
        formGroup [
            textarea [
                Id lbl
                Placeholder "Enter the unformatted text content."
                HTMLAttr.Rows 10
                Key (sprintf "%O" blockId)
                ClassName "form-control content-area"
                DefaultValue txt
                OnChange (fun ev -> dispatch (BlockContentTypeChanged(scopeId, blockId, (RawText !!ev.target?value))))
            ] []
        ]
    | PageNavigation -> p [ ClassName "form-control-static" ] [ str "This section is auto-generated. It will link to all siblings of the page." ]
    | YouTubeEmbed (link, height, width, full) ->
         panel "success" "YouTube embed" [
             label [ HtmlFor (lbl+"-embed") ] [ str "Youtube Embed Link" ]    
             textInput (lbl+"-embed") "Enter the embed link." (fun str -> YouTubeLinkChanged(scopeId, blockId, str)) dispatch link (Some 200.)
             label [ HtmlFor (lbl+"-h") ] [ str "Height" ]    
             textInput (lbl+"-h") "Enter the height." (fun str -> YouTubeHeightChanged(scopeId, blockId, str)) dispatch height (Some 8.)
             label [ HtmlFor (lbl+"-w") ] [ str "Width" ]    
             textInput (lbl+"-w") "Enter the width." (fun str -> YouTubeWidthChanged(scopeId, blockId, str)) dispatch width (Some 8.) 
             label [ HtmlFor (lbl+"-fs") ] [ str "Allow Full Screen?" ]  
             input [
                HTMLAttr.Type "checkbox";
                HTMLAttr.Id (lbl+"-fs");
                OnChange (fun ev -> dispatch (YouTubeFullScreenChanged(scopeId, blockId, !!ev.target?``checked``)))
                Checked full;
            ]   
         ]


let renderBlock dispatch allowDeleteFirst max (scopeId: Guid) i (block: Block) =
    let labeller lbl = sprintf "%s-%O" lbl block.Id
    [
        yield div [ ClassName "valign" ] [ 
            yield h4 [] [ str "Block" ]
            if i > 0 || allowDeleteFirst then
                yield button [ 
                    ClassName "btn btn-danger"; 
                    OnClick (fun _ -> dispatch (DeleteBlock(scopeId, block.Id)))
                    HTMLAttr.Type "button"
                ] [ str "Delete" ]
            if i > 0 then
                yield button [ 
                    ClassName "btn btn-primary"
                    HTMLAttr.Type "button"
                    OnClick (fun _ -> dispatch (MoveBlock (scopeId, block.Id, Up)))
                ] [ str "Move Up" ] 
            if max >= 0 && i < max then
                yield button [ 
                    ClassName "btn btn-primary"
                    HTMLAttr.Type "button"
                    OnClick (fun _ -> dispatch (MoveBlock (scopeId, block.Id, Down)))
                ] [ str "Move Down" ] 
        ]
        yield hr []
        match block.Header with
        | None ->
            yield div [ ClassName "toggle" ] [
                yield div [ ClassName "alert alert-info lr" ] [ 
                    str "This block does not have a header. " 
                    button [ 
                        ClassName "btn btn-info"; 
                        HTMLAttr.Type "button"
                        OnClick (fun _ -> dispatch (ShowBlockHeader(scopeId, block.Id))) 
                    ] [ str "Add header" ]
                ]
            ]
        | Some (accent, content) ->
            let lbl = labeller "header_colour"
            yield label [ HtmlFor lbl ] [ str "Header Colour" ]
            yield select [
                ClassName "form-control"
                Id lbl
                Placeholder "Select the header colour." 
                HTMLAttr.Value (formatAccent accent) 
                OnChange (fun ev -> dispatch (BlockHeaderColourChanged(scopeId, block.Id, (parseAccent !!ev.target?value))))
            ] ([Inherited; Light; Dark; Turquoise; DarkRed; Blue; Grey] |> List.map (fun acc ->
                option [] [ str (formatAccent acc) ]))
            
            match content with
            | RawText text ->
                let lbl = labeller "header_text"
                yield label [ HtmlFor lbl ] [ str "Header Text" ]
                yield textInput lbl "Enter the header text." (fun str -> BlockHeaderTextChanged(scopeId, block.Id, str)) dispatch text (Some 100.)
            | _ -> yield str "Unsupported header"

            yield hr []

            yield div [ ClassName "toggle" ] [
                yield button [ 
                    ClassName "btn btn-warning"; 
                    HTMLAttr.Type "button"
                    OnClick (fun _ -> dispatch (HideBlockHeader(scopeId, block.Id))) 
                ] [ str "Remove header" ]
            ]

        let lbl = labeller "block_justification"
        yield formGroup [
            label [ HtmlFor lbl ] [ str "Justification" ]
            select [
                ClassName "form-control"
                Id lbl
                Placeholder "Select the justification." 
                HTMLAttr.Value (formatJustification block.Justification) 
                OnChange (fun ev -> dispatch (BlockAlignmentChanged (scopeId, block.Id, parseJustification !!ev.target?value)))
            ] ([LeftAligned; Justification.Centered] |> List.map (fun j -> option [] [ str (formatJustification j) ]))
        ]

        let lbl = labeller "block_bg"
        yield formGroup [
            label [ HtmlFor lbl ] [ str "Block Background Colour" ]
            textInput lbl "Enter the block background." (fun str -> BlockBackgroundColourChanged (scopeId, block.Id, str)) dispatch (getFromTheme (Background "") block.Theme) (Some 50.)
        ]  

        let lbl = labeller "block_colour"
        yield formGroup [
            label [ HtmlFor lbl ] [ str "Block Text Colour" ]
            textInput lbl "Enter the text colour." (fun str -> BlockTextColourChanged (scopeId, block.Id, str)) dispatch (getFromTheme (IThemeItem.Colour "") block.Theme) (Some 50.)
        ]  

        let lbl = labeller "block_fontsize"
        yield formGroup [
            label [ HtmlFor lbl ] [ str "Block Font Size" ]
            textInput lbl "Enter the font size." (fun str -> BlockFontSizeChanged (scopeId, block.Id, str)) dispatch (getFromTheme (IThemeItem.FontSize "") block.Theme) (Some 10.)
        ]

        let lbl = labeller "block_content_type"
        yield formGroup [
            yield label [ HtmlFor lbl ] [ str "Block Content Type" ]
            yield select [
                ClassName "form-control"
                Id lbl
                Placeholder "Select the block content type." 
                HTMLAttr.Value (formatContent block.Content) 
                OnChange (fun ev -> dispatch (BlockContentTypeChanged(scopeId, block.Id, (parseFormatContent !!ev.target?value))))
            ] ([RawText ""; Markdown ""; PageNavigation; YouTubeEmbed("", "", "",false)] |> List.map (fun acc ->
                option [] [ str (formatContent acc) ])) 
        ]

        let lbl = labeller "block_content"
        yield formGroup [
            yield label [ HtmlFor lbl ] [ str "Block Content " ]
            yield renderContent scopeId (block.Id) lbl dispatch (block.Content)
            match block.Content with
            | Markdown _ -> 
                yield button [
                    ClassName "btn btn-primary"
                    HTMLAttr.Type "button"
                    OnClick (fun _ -> dispatch (ShowFilePicker(scopeId, block.Id, LinkEmbed.InsideBlock)))
                ] [ str "Insert Uploaded File Link" ]
            | _ -> ()
        ]            
    ]

let private renderTextBlockButtons (id: Guid) dispatch targetPanel =
    footer [] [
        button [ 
            ClassName "btn btn-info"
            HTMLAttr.Type "button"
            OnClick (fun _ -> dispatch (AddToTextBlock (id, targetPanel)))
        ] [ str "Add block" ]
    ]

let private renderTextBlockNoButtons (dispatch: Msg->unit) allowDeleteFirst (id: Guid) (blocks: Block list) =
    (blocks |> List.mapi (renderBlock dispatch allowDeleteFirst (blocks.Length - 1) id) |> List.concat) 

let private renderTextBlock (dispatch: Msg->unit) (id: Guid) (blocks: Block list) panel =
    renderTextBlockNoButtons dispatch true id blocks @ [renderTextBlockButtons id dispatch panel]

let private renderShape scopeId thisShape currentShape (dispatch: Msg -> unit) =
    let selected = thisShape = currentShape
    let label, className =
        match thisShape with
        | Rectangle -> "Rectangle", "img"
        | Circle -> "Circle", "img-circle"
        | Thumbnail -> "Thumbnail", "img-thumbnail"
        | RoundedRectangle -> "Rounded", "img-rounded"
    
    div [
        OnClick (fun _ -> dispatch (ChangeCardShape (scopeId, thisShape)))
        Style [
            Cursor "pointer"
        ]
    ] [
        h4 [ Style [ TextDecoration (if selected then "underline" else "none") ] ] [ str label ]
        img [ 
            Src "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9InllcyI/PjxzdmcgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB3aWR0aD0iMTQwIiBoZWlnaHQ9IjE0MCIgdmlld0JveD0iMCAwIDE0MCAxNDAiIHByZXNlcnZlQXNwZWN0UmF0aW89Im5vbmUiPjwhLS0KU291cmNlIFVSTDogaG9sZGVyLmpzLzE0MHgxNDAKQ3JlYXRlZCB3aXRoIEhvbGRlci5qcyAyLjYuMC4KTGVhcm4gbW9yZSBhdCBodHRwOi8vaG9sZGVyanMuY29tCihjKSAyMDEyLTIwMTUgSXZhbiBNYWxvcGluc2t5IC0gaHR0cDovL2ltc2t5LmNvCi0tPjxkZWZzPjxzdHlsZSB0eXBlPSJ0ZXh0L2NzcyI+PCFbQ0RBVEFbI2hvbGRlcl8xNjQ1YmQyMGMxMyB0ZXh0IHsgZmlsbDojQUFBQUFBO2ZvbnQtd2VpZ2h0OmJvbGQ7Zm9udC1mYW1pbHk6QXJpYWwsIEhlbHZldGljYSwgT3BlbiBTYW5zLCBzYW5zLXNlcmlmLCBtb25vc3BhY2U7Zm9udC1zaXplOjEwcHQgfSBdXT48L3N0eWxlPjwvZGVmcz48ZyBpZD0iaG9sZGVyXzE2NDViZDIwYzEzIj48cmVjdCB3aWR0aD0iMTQwIiBoZWlnaHQ9IjE0MCIgZmlsbD0iI0VFRUVFRSIvPjxnPjx0ZXh0IHg9IjQzLjUiIHk9Ijc0LjgiPjE0MHgxNDA8L3RleHQ+PC9nPjwvZz48L3N2Zz4="
            Style [
                Height "140px"
                Width "140px"
                Border (if selected then "solid 2px #4c6085" else "none")
            ]
            ClassName className
        ]
    ]

let filePicker dispatch show closer (files: (string * FileMeta list) list) (selectedFile: FileMeta option) =
    let genOption file = option [ Value file.Name ] [ str <| sprintf "%s - %d KB - uploaded on %s" file.Name (file.Size / 1000) (file.DateUploaded.ToString("dd/MMM/yyyy")) ]
    let allFiles = files |> List.collect snd
    if show then
        modal "Choose an uploaded file" dispatch closer [
            div [ ClassName "centerflex" ] [
                yield select [
                    OnChange (fun ev -> dispatch (SelectFile (allFiles |> List.find (fun x -> x.Name = !!ev.target?value))))
                    Style [
                        Height "34px"
                        MarginRight "10px"
                        Width "100%"
                    ]
                ] (files |> List.map (fun (band, items) -> optgroup [ Label band ] (items |> List.map genOption)))
                if selectedFile.IsNone || selectedFile.Value.Thumbnail = "" then
                    ()
                else
                    yield img [ 
                        HTMLAttr.Height "32px"
                        HTMLAttr.Width "32px"
                        Alt "thumbnail"
                        Src (sprintf "data:image/png;base64,%s" selectedFile.Value.Thumbnail)
                    ]
                yield button [
                    ClassName "btn btn-primary"
                    HTMLAttr.Type "button"
                    Style [
                        MarginLeft "10px"
                    ]
                    Disabled (selectedFile.IsNone)
                    OnClick (fun _ -> dispatch InsertButtonClicked)
                ] [
                    str "Insert Link"
                ]
            ]  
        ] []
    else
        []

let private viewEditPage user pageState dispatch =    
    let page = pageState.Page
    [ 
        yield! filePicker dispatch pageState.ShowingFilePicker HideFilePicker pageState.Files pageState.SelectedFile
        yield form [] [
            formGroup [
                label [ HtmlFor "title"; ClassName "mandatory" ] [ str "Page Title" ]
                textInput "title" "Enter the page title." MainTitleChanged dispatch page.Location.Name (Some 50.)
            ]
            formGroup [
                input [
                    HTMLAttr.Type "checkbox"
                    HTMLAttr.Id "landingPage"
                    OnChange (fun _ -> dispatch ToggleLandingPage)
                    Checked page.IsLandingPage
                    Style [MarginRight "0.5em"]
                ]
                label [ HtmlFor "landingPage"] [ str "Is Landing Page"]
            ]
            formGroup [
                yield label [ HtmlFor "id"; ClassName "mandatory" ] [ str "Page Identifier" ]
                yield textInput "id" "Enter the page id." (Slug >> MainIdentifierChanged) dispatch (Slug.get page.Location.Id) (Some 500.)

                if (Slug.get page.Location.Id) <> pageState.IdentifierWhenLoaded && not (String.IsNullOrWhiteSpace(pageState.IdentifierWhenLoaded)) then
                    yield div [ ClassName "alert alert-warning"; Role "alert" ] [ str "The identifier has been changed. This may cause this item to appear in different menus and navigation controls." ]

                if pageState.WarnIdentifier then
                    yield div [ ClassName "alert alert-warning"; Role "alert" ] [ str "This page already exists and will be replaced after saving." ]
                
                if locationContainsSpaces page.Location then                                
                    yield div [ ClassName "alert alert-danger"; Role "alert" ] [ str "The identifier cannot contain spaces" ]                
            ]
            panel "default" "Default Theme" [
                formGroup [
                    label [ HtmlFor "theme_background" ] [ str "Page Background Colour" ]
                    textInput "theme_background" "Enter the page background." PageBackgroundColourChanged dispatch (getFromTheme (Background "") page.Theme) (Some 50.)
                ]  
                formGroup [
                    label [ HtmlFor "theme_colour" ] [ str "Default Text Colour" ]
                    textInput "theme_colour" "Enter the text colour." DefaultTextColourChanged dispatch (getFromTheme (IThemeItem.Colour "") page.Theme) (Some 50.)
                ]  
                formGroup [
                    label [ HtmlFor "theme_fontSize" ] [ str "Default Font Size" ]
                    textInput "theme_fontSize" "Enter the font size." DefaultFontSizeChanged dispatch (getFromTheme (IThemeItem.FontSize "") page.Theme) (Some 50.)
                ]             
            ]
            div [] [
                let max = page.TextBlocks.Length - 1
                for (i,block) in page.TextBlocks |> List.indexed do
                    let labeller lbl = sprintf "%s-%O" lbl (TextBlock.Id block)
                    yield panelWithTitleContent "primary" [ 
                            yield str "Container"; 
                            yield button [ 
                                ClassName "btn btn-danger"
                                HTMLAttr.Type "button"
                                OnClick (fun _ -> dispatch (DeleteTextBlock <| TextBlock.Id block))
                            ] [ str "Delete" ] 
                            
                            if i > 0 then
                                yield button [ 
                                    ClassName "btn btn-primary"
                                    HTMLAttr.Type "button"
                                    OnClick (fun _ -> dispatch (MoveTextBlock (TextBlock.Id block, Up)))
                                ] [ str "Move Up" ] 
                            if max >= 0 && i < max then
                                yield button [ 
                                    ClassName "btn btn-primary"
                                    HTMLAttr.Type "button"
                                    OnClick (fun _ -> dispatch (MoveTextBlock (TextBlock.Id block, Down)))
                                ] [ str "Move Down" ] 
                        ] [     
                        formGroup [
                            label [ HtmlFor (labeller "block_type") ] [ str "Container Type" ]
                            select [
                                ClassName "form-control"
                                Id (labeller "block_type") 
                                Placeholder "Select the container type." 
                                HTMLAttr.Value (blockType block) 
                                OnChange (fun ev -> dispatch (BlockTypeChanged(parseBlockType (TextBlock.Id block) (!!ev.target?value))))
                            ] [
                                option [] [ str <| blockType (SingleColumn (Guid.Empty,[]))]
                                option [] [ str <| blockType (TwoColumn (Guid.Empty,[],[]))]
                                option [] [ str <| blockType (MainAndSideBar (Guid.Empty,[],[]))]
                                option [] [ str <| blockType (CardList (Guid.Empty,ImageShape.Rectangle,[]))]
                            ]
                            div [ ClassName "inner-block clearfix" ] [
                                match block with
                                | MainAndSideBar (id, main, sidebar) ->
                                    yield div [ ClassName "col-sm-8" ] [
                                        panel "info" "Main Content" (renderTextBlock dispatch id main Panel1)                                
                                    ]
                                    yield div [ ClassName "col-sm-4" ] [
                                        panel "default" "Sidebar" (renderTextBlock dispatch id sidebar Panel2)            
                                    ]
                                | SingleColumn (id, main) -> 
                                    yield div [ ClassName "col-sm-12" ] [
                                        panel "info" "Main Content" (renderTextBlock dispatch id main Panel1)                                  
                                    ]
                                | TwoColumn (id, left, right) ->
                                    yield div [ ClassName "col-sm-6" ] [
                                        panel "info" "Left Content" (renderTextBlock dispatch id left Panel1)                                  
                                    ]
                                    yield div [ ClassName "col-sm-6" ] [
                                        panel "default" "Right Content" (renderTextBlock dispatch id right Panel2)            
                                    ]
                                | CardList (id, shape, cards) ->
                                    yield div [ 
                                            ClassName "centerflex"
                                            Style [
                                                TextAlign TextAlignOptions.Center
                                                Padding "10px"
                                            ]
                                        ] [ 
                                        renderShape id Rectangle shape dispatch
                                        renderShape id RoundedRectangle shape dispatch
                                        renderShape id Circle shape dispatch
                                        renderShape id Thumbnail shape dispatch
                                    ]
                                    for (img, cardContent) in cards do
                                        yield div [ ClassName "col-sm-3" ] [
                                            panelWithTitleContent "primary" [ 
                                                str "Image"; 
                                            ] [
                                                yield formGroup [
                                                    label [ HtmlFor <| sprintf "imageUrl-%O" cardContent.Id ] [ str "Image Link" ]
                                                    textInput (sprintf "imageUrl-%O" cardContent.Id) "Enter the image link." (fun str -> CardUrlChanged (id, cardContent.Id, str)) dispatch (img.Url) (Some 500.)
                                                ]
                                                yield button [
                                                    ClassName "btn btn-primary btn-block"
                                                    HTMLAttr.Type "button"
                                                    Style [
                                                        Width "100%"
                                                    ]
                                                    OnClick (fun _ -> dispatch (ShowFilePicker(id, cardContent.Id, LinkEmbed.CardImage)))
                                                ] [ str "Insert Uploaded Image Link" ]
                                                yield formGroup [
                                                    label [ HtmlFor <| sprintf "imageAlt-%O" cardContent.Id ] [ str "Image Alt Text" ]
                                                    textInput (sprintf "imageAlt-%O" cardContent.Id) "Enter the alt image text." (fun str -> CardAltChanged (id, cardContent.Id, str)) dispatch (img.Alt) (Some 200.)
                                                ] 
                                                yield formGroup [
                                                    label [ HtmlFor <| sprintf "imageHover-%O" cardContent.Id ] [ str "Image Hover Text" ]
                                                    textInput (sprintf "imageHover-%O" cardContent.Id) "Enter the hover text." (fun str -> CardHoverTextChanged (id, cardContent.Id, str)) dispatch (img.HoverText) (Some 500.)
                                                ] 
                                            ]
                                        ]
                                        yield div [ ClassName "col-sm-9" ] [
                                            panel "info" "Main Content" (renderTextBlockNoButtons dispatch false id [cardContent])   
                                        ]

                                    // footer for cards
                                    yield footer [] [
                                        button [ 
                                            ClassName "btn btn-info"
                                            HTMLAttr.Type "button"
                                            OnClick (fun _ -> dispatch (AddToTextBlock (id, Panel1)))
                                        ] [ str "Add card" ]
                                    ]
                            ]
                        ]
                    ]
            ]
        ]
        yield footer [] [
            button [ 
                ClassName "btn btn-primary"
                OnClick (fun _ -> dispatch ReturnToPageList)
                HTMLAttr.Type "button"
            ] [ str "Go Back" ]
            button [ 
                ClassName "btn btn-default"
                OnClick (fun _ -> dispatch PreviewPage)
                HTMLAttr.Type "button"
            ] [ str "Preview this Page" ]
            button [ 
                ClassName "btn btn-success"
                OnClick (fun _ -> dispatch SavePage)
                HTMLAttr.Type "button"
                Disabled (String.IsNullOrWhiteSpace <| Slug.get page.Location.Id || String.IsNullOrWhiteSpace page.Location.Name || locationContainsSpaces page.Location)
            ] [ str "Save the Page" ]           
            button [ 
                ClassName "btn btn-info"
                OnClick (fun _ -> dispatch AddToPage)
                HTMLAttr.Type "button"
            ] [ str "Add container" ]
            button [ 
                ClassName "btn btn-danger"
                OnClick (fun _ -> dispatch ConfirmDeletePage)
                HTMLAttr.Type "button"
            ] [ str "Delete this page" ]
        ]

        match pageState.Preview with
        | None -> ()
        | Some prev -> 
            yield! modal "Preview" dispatch ClosePreview (Scaffold.renderPage (Generic prev) None) []
         
    ]

let private viewListPages (user: JWT) (pages: PageMeta list) dispatch =
    [
        yield div [ ClassName "valign" ] [
            yield h2 [ Style [ MarginRight "10px" ] ] [ str "Pages" ] 
            yield button [ 
                ClassName "btn btn-default" 
                HTMLAttr.Type "button"
                OnClick (fun _ -> dispatch AddNewPage)
            ] [ str "Add new Page" ]
        ]
        match pages.Length with
        | 0 -> 
            yield p [ ClassName "bg-info" ] [ str "You have not yet added any pages. Click the 'Add New' button to add your first." ]
        | n ->
            yield div [] [ str <| sprintf "There %s %d maintained pag%s." (be n) n (plural "e" n) ]
            yield table [ ClassName "table" ] [
                thead [] [
                    tr [] [
                        th [] [ str "Page Name" ]
                        th [] []
                    ]
                ]
                tbody [] [
                    yield! pages |> List.map (fun meta -> 
                        tr [] [ 
                            td [] [ 
                                yield str meta.Location.Name 
                                
                                if meta.IsLandingPage then
                                    yield span [
                                        ClassName "label label-info"
                                        Style [MarginLeft "1em"]
                                    ] [str "Landing Page"]
                            ]
                            td [] [ 
                                a [ Href (sprintf "#areas/%s/pages/%s" (TenantRef.get user.Account.Tenant) (Slug.get meta.Location.Id)); Target "_blank" ] [ str "Go to" ]
                                a [ jsVoid; OnClick (fun _ -> dispatch (PageClicked(meta.Location.Id, Edit))) ] [ str "Edit" ]
                                a [ jsVoid; OnClick (fun _ -> dispatch (PageClicked(meta.Location.Id, Delete))) ] [ str "Delete" ]
                            ]                                                                    
                        ])
                ]
            ]
            
    ] 



let view (model: Model) dispatch =
    let toggle renderer =
        function
        | Loading _ -> [ str "Loading..." ]
        | Loaded x -> renderer model.User x dispatch
    
    let ret =
        match model.Action with
        | Editing state -> toggle viewEditPage state            
        | Listing state -> toggle viewListPages state
    
    match model.ShowConfirmDelete with
    | false -> ret
    | true -> 
        let m = 
            modal "Confirm Deletion" dispatch CancelDeletion [
                p [] [ str "Are you sure you wish to delete this page?" ]
            ] [
                button [ 
                    ClassName "btn btn-danger"; 
                    HTMLAttr.Type "button"
                    OnClick (fun _ -> dispatch ConfirmedPageDeletion) 
                ] [ str "Confirm Deletion" ]
                button [ 
                    ClassName "btn btn-default"; 
                    HTMLAttr.Type "button"
                    OnClick (fun _ -> dispatch CancelDeletion); Keyboard.onEscape CancelDeletion dispatch
                ] [ str "Cancel" ]
            ]
        m@ret

let replaceTextBlock (page: GenericPage) replaceWith =
    { page with 
        TextBlocks = 
            [
                for block in page.TextBlocks do
                    if TextBlock.Id block <> TextBlock.Id replaceWith then 
                        yield block
                    else
                        yield replaceWith
            ]
    }

let swapTextBlock (page: GenericPage) block direction =
    { page with TextBlocks = Elements.swapById (TextBlock.Id) direction block (page.TextBlocks) }

let swapBlock id direction blocks =
    Elements.swapId (Block.GetId) direction id blocks

let removeTextBlock (page: GenericPage) id =
    { page with 
        TextBlocks = 
            page.TextBlocks |> List.filter (TextBlock.Id >> ((<>) id))
    }

let replaceBlock repl id (blocks: Block list) =
    [
         for block in blocks do
            if block.Id <> id then
                yield block
            else
                yield repl block
    ]

let removeBlock id (blocks: Block list) =
    blocks |> List.filter (Block.GetId >> ((<>) id))

let replaceContentBlock replacementBlock = replaceBlock (fun block -> { block with Content = replacementBlock })

let findTextBlock (page: GenericPage) id =
    page.TextBlocks |> List.find (fun x -> TextBlock.Id x = id)

let findContentBlock (blocks: Block list) id =
    blocks |> List.find (fun x -> x.Id = id)

let replaceStyle (repl: IThemeItem) (theme: Theme) =
    let matcher = getThemeMatcher repl
    List.Cons(repl, theme |> List.filter(matcher >> FSharp.Core.Option.isNone))

let newBlock() =
    {
        Id = Guid.NewGuid()
        Header = None
        Content = RawText ""
        Theme = []
        Justification = Justification.LeftAligned
    }

let newImageBlock() =
    {
        Url = "";
        HoverText = ""
        Alt = "";
    }

let appendBlock list = list @ [ newBlock() ]

/// Reusable sugar method to make a perform a change to the edit page
let private editing model modifier cmd success =
     match model.Action with
        | Editing (Loaded pageState) ->  
           modifier model pageState, (cmd model pageState), (success pageState)
        | _ -> failwith "Invalid action"

/// Reusable sugar method to make a perform a change to the edit page that does not stop editing
let private editingAction model modifier cmd success =
     editing model (fun m pageState -> { m with Action = Editing (Loaded(modifier pageState)) }) cmd success

/// Reusable sugar method to make a perform a change to the edit page without a change of success message
let private editingActionU model modifier cmd = editingAction model modifier cmd (fun _ -> Unchanged)

/// Reusable sugar method to make a change to a list of blocks within a container
let modifyBlockList model scope id replacer =
    editingActionU model (fun ps ->
            let newBlock = 
                findTextBlock ps.Page scope 
                |> TextBlock.MapList (replacer id)
            { ps with Page = replaceTextBlock ps.Page newBlock }
            ) (fun _ _ -> [])

/// Reusable sugar method to make a change to a specific block within a container
let private modifyBlock model scope id replacer =
    modifyBlockList model scope id (replaceBlock replacer)

let update (msg:Msg) model : Model*Cmd<Msg>* SuccessMessageChange =     
    match msg with
    | CancelDeletion -> { model with ShowConfirmDelete = false }, [], Unchanged
    | ConfirmedPageDeletion -> editingActionU model id (fun _ x -> deletePageCmd model.User x.Page.Location.Id)
    | PageDeleted ->
        editingAction model id (fun _ _ -> Cmd.ofMsg ReturnToPageList) (fun x -> Show (sprintf "The page '%s' was deleted." x.Page.Location.Name))
    | ConfirmDeletePage -> editing model (fun m _ -> { m with ShowConfirmDelete = true }) (fun _ _ -> []) (fun _ -> Unchanged)
    | AddNewPage ->
        match model.Action with
        | Listing (Loaded _) ->  
            let newPage = 
                { 
                    IdentifierWhenLoaded = ""
                    WarnIdentifier = false
                    Page = 
                        {
                            Location = { Id = Slug ""; Name = "New Page"; }
                            Tenant = model.User.Account.Tenant
                            Header =  None
                            BannerImage = None
                            TextBlocks = []
                            Theme = [ Background "white"]
                            PageWidth = PageWidth.Centered
                            IsLandingPage = false
                        } 
                    Preview = None
                    ShowingFilePicker = false
                    Files = []
                    SelectedFile = None
                    LinkTarget = None
                }
            { model with Action = Editing (Loaded newPage) }, [], Unchanged
        | _ -> failwith "Invalid action"
    | LoadedFiles files ->
        let grouped =
            let today = System.DateTime.Today
            files |> List.groupBy (fun x -> 
                match x.DateUploaded.Date with 
                | d when d = today -> "Uploaded today"
                | d when d >= today.AddDays(-7.) -> "Uploaded this week"
                | d when d >= today.AddDays(-31.) -> "Uploaded within a month"
                | _ -> "Older"
            )
        let selected = if files.Length > 0 then Some files.[0] else None
        editingActionU model (fun ps -> { ps with ShowingFilePicker = true; Files = grouped; SelectedFile = selected }) (fun _ _ -> [])
    | SelectFile file ->
        editingActionU model (fun ps -> { ps with SelectedFile = Some file }) (fun _ _ -> [])
    | ShowFilePicker (scopeId, id, target) ->
        editingActionU model (fun ps -> { ps with LinkTarget = Some (scopeId, id, target) }) (fun _ _ -> getFilesCmd model.User (match target with LinkEmbed.CardImage -> true | _ -> false))
    | HideFilePicker ->
        editingActionU model (fun ps -> { ps with ShowingFilePicker = false }) (fun _ _ -> [])
    | InsertButtonClicked ->
        editingActionU model (fun ps -> { ps with SelectedFile = None }) (fun m ps -> 
            match ps.LinkTarget with
            | None -> []
            | Some (scopeId, id, embed) ->
                let encoded = ps.SelectedFile.Value.Name |> Fable.Core.JS.encodeURI
                let link = Urls.GetFile (TenantRef.get m.User.Account.Tenant) encoded
                
                // are we in a card or a markdown block?
                match findTextBlock ps.Page scopeId, embed with
                | CardList _, LinkEmbed.CardImage ->
                    Cmd.batch [Cmd.ofMsg (CardUrlChanged(scopeId, id, link)); Cmd.ofMsg HideFilePicker ]
                | _ ->
                    Cmd.batch [Cmd.ofMsg (InsertFileLink(scopeId, id, ps.SelectedFile.Value.Image, link)); Cmd.ofMsg HideFilePicker ])
    | InsertFileLink (scopeId, id, isImage, link) ->
        modifyBlock model scopeId id (fun block -> 
            let linkText = 
                if isImage then
                    sprintf @"![img](%s ""img"")" link
                else
                    sprintf @"[link](%s)" link
            let replacement =
                match block.Content with
                | Markdown str -> Markdown (str + linkText)
                | x -> x
            { block with Content = replacement })
    | SavePage -> editingActionU model id (fun m ps -> postSavePageCmd m.User ps.Page (Slug ps.IdentifierWhenLoaded))
    | PageSaved ->
        editingAction model id (fun _ _ -> Cmd.ofMsg ReturnToPageList) (fun x -> Show (sprintf "Your changes to the page '%s' have been saved." x.Page.Location.Name))
    | AddToPage ->
        editingActionU model (fun ps -> { ps with Page = { ps.Page with TextBlocks = ps.Page.TextBlocks @ [SingleColumn (Guid.NewGuid(), [])] } }) (fun _ _ -> [])
    | AddToTextBlock (scope, target) ->
        editingActionU model (fun ps ->
            let newBlock = 
                match findTextBlock ps.Page scope with
                | SingleColumn (id, blocks) -> SingleColumn (id, appendBlock blocks)
                | TwoColumn (id, l, r) when target = Panel1 -> TwoColumn (id, appendBlock l, r)
                | TwoColumn (id, l, r) when target = Panel2 -> TwoColumn (id, l, appendBlock r)
                | MainAndSideBar (id, l, r) when target = Panel1 -> MainAndSideBar (id, appendBlock l, r)
                | MainAndSideBar (id, l, r) when target = Panel2 -> MainAndSideBar (id, l, appendBlock r)
                | CardList (id, shape, cards) -> CardList (id, shape, (cards @ [ newImageBlock(), newBlock()]))
                | x -> x 
            { ps with Page = replaceTextBlock ps.Page newBlock }) (fun _ _ -> [])
    | ChangeCardShape (scope, newShape) ->
        editingActionU model (fun ps ->
            let newBlock =
                match findTextBlock ps.Page scope with
                | CardList (id, _, cards) -> CardList(id, newShape, cards)
                | _ -> failwith "Expected card list"
            { ps with Page = replaceTextBlock ps.Page newBlock }) (fun _ _ -> [])
    | CardUrlChanged (scope, cardId, url) ->
        editingActionU model (fun ps ->
            let newBlock =
                match findTextBlock ps.Page scope with
                | CardList (id, shape, cards) -> 
                    let newCards =
                        [
                            for (img, content) as c in cards do
                                if content.Id = cardId then
                                    yield { img with Url = url }, content
                                else
                                    yield c
                        ]
                    CardList(id, shape, newCards)
                | _ -> failwith "Expected card list"
            { ps with Page = replaceTextBlock ps.Page newBlock }) (fun _ _ -> [])
    | CardAltChanged (scope, cardId, alt) ->
        editingActionU model (fun ps ->
            let newBlock =
                match findTextBlock ps.Page scope with
                | CardList (id, shape, cards) -> 
                    let newCards =
                        [
                            for (img, content) as c in cards do
                                if content.Id = cardId then
                                    yield { img with Alt = alt }, content
                                else
                                    yield c
                        ]
                    CardList(id, shape, newCards)
                | _ -> failwith "Expected card list"
            { ps with Page = replaceTextBlock ps.Page newBlock }) (fun _ _ -> [])
    | CardHoverTextChanged (scope, cardId, text) ->
        editingActionU model (fun ps ->
            let newBlock =
                match findTextBlock ps.Page scope with
                | CardList (id, shape, cards) -> 
                    let newCards =
                        [
                            for (img, content) as c in cards do
                                if content.Id = cardId then
                                    yield { img with HoverText = text }, content
                                else
                                    yield c
                        ]
                    CardList(id, shape, newCards)
                | _ -> failwith "Expected card list"
            { ps with Page = replaceTextBlock ps.Page newBlock }) (fun _ _ -> [])
    | BlockTypeChanged block -> editingActionU model (fun ps -> { ps with Page = replaceTextBlock ps.Page block }) (fun _ _ -> [])
    | BlockContentTypeChanged (scope, id, replacementBlock) -> modifyBlockList model scope id (replaceContentBlock replacementBlock)
    | HideBlockHeader (scope, id) -> modifyBlock model scope id (fun block -> { block with Header = None })
    | DeleteBlock (scope, id) -> modifyBlockList model scope id removeBlock
    | DeleteTextBlock id ->
        editingActionU model (fun ps ->
            { ps with Page = removeTextBlock ps.Page id }
            ) (fun _ _ -> [])
    | MoveTextBlock (id, direction) ->
        editingActionU model (fun ps ->
            let block = findTextBlock ps.Page id
            { ps with Page = swapTextBlock ps.Page block direction }
            ) (fun _ _ -> [])
    | MoveBlock (scope, id, direction) ->
        modifyBlockList model scope id (fun i -> swapBlock i direction)
    | BlockBackgroundColourChanged (scope, id, colour) ->
        modifyBlock model scope id (fun block -> { block with Theme = replaceStyle (Background colour) block.Theme })
    | BlockTextColourChanged (scope, id, colour) ->
        modifyBlock model scope id (fun block -> { block with Theme = replaceStyle (Colour colour) block.Theme })
    | BlockFontSizeChanged (scope, id, size) ->
        modifyBlock model scope id (fun block -> { block with Theme = replaceStyle (FontSize size) block.Theme })
    | ShowBlockHeader (scope, id) ->
        modifyBlock model scope id (fun block -> { block with Header = Some (ColourAccent.Inherited, RawText "") })
    | BlockHeaderColourChanged (scope, id, newColour) ->
        modifyBlock model scope id (fun block -> 
                    match block.Header with
                    | None -> failwith "Invalid header state"
                    | Some (_,header) ->
                        { block with Header = Some (newColour, header) })
    | BlockHeaderTextChanged (scope, id, text) ->
         modifyBlock model scope id (fun block -> 
                    match block.Header with
                    | None -> failwith "Invalid header state"
                    | Some (colour,_) ->
                        { block with Header = Some (colour, RawText text) })
    | YouTubeLinkChanged (scope, id, text) ->
         modifyBlock model scope id (fun block -> 
                let newContent =
                    match block.Content with
                    | YouTubeEmbed (_,h,w,fs) -> YouTubeEmbed (text, h, w,fs)
                    | _ -> failwith "Invalid youtube field state"
                { block with Content = newContent })
    | YouTubeHeightChanged (scope, id, text) ->
         modifyBlock model scope id (fun block -> 
                let newContent =
                    match block.Content with
                    | YouTubeEmbed (link,_,w,fs) -> YouTubeEmbed (link, text, w,fs)
                    | _ -> failwith "Invalid youtube field state"
                { block with Content = newContent })
    | YouTubeWidthChanged (scope, id, text) ->
         modifyBlock model scope id (fun block -> 
                let newContent =
                    match block.Content with
                    | YouTubeEmbed (link,h,_,fs) -> YouTubeEmbed (link, h, text,fs)
                    | _ -> failwith "Invalid youtube field state"
                { block with Content = newContent })
    | YouTubeFullScreenChanged (scope, id, fs) ->
         modifyBlock model scope id (fun block -> 
                let newContent =
                    match block.Content with
                    | YouTubeEmbed (link,h,w,_) -> YouTubeEmbed (link, h,w,fs)
                    | _ -> failwith "Invalid youtube field state"
                { block with Content = newContent })
    | BlockAlignmentChanged (scope, id, justification) ->
        modifyBlock model scope id (fun block -> { block with Justification = justification })
    | MainTitleChanged title ->
        editingActionU model (fun ps -> { ps with Page = { ps.Page with Location = { ps.Page.Location with Name = title }}}) (fun _ _ -> [])
    | MainIdentifierChanged identifier ->
        editingActionU model (fun ps -> { ps with Page = { ps.Page with Location = { ps.Page.Location with Id = identifier }}}) (fun m _ -> getPageUniquenessCmd (m.User) identifier)
    | IdentifierUniquenessChecked unique ->
        editingActionU model (fun ps -> { ps with WarnIdentifier = unique}) (fun _ _ -> [])
    | PageBackgroundColourChanged colour ->
        editingActionU model (fun ps -> { ps with Page = { ps.Page with Theme = replaceStyle (Background colour) ps.Page.Theme } }) (fun _ _ -> [])
    | DefaultTextColourChanged colour ->
        editingActionU model (fun ps -> { ps with Page = { ps.Page with Theme = replaceStyle (Colour colour) ps.Page.Theme } }) (fun _ _ -> [])
    | DefaultFontSizeChanged fs ->
        editingActionU model (fun ps -> { ps with Page = { ps.Page with Theme = replaceStyle (FontSize fs) ps.Page.Theme } }) (fun _ _ -> [])
    | ReturnToPageList ->
        { model with Action = Listing (Loading()); ShowConfirmDelete = false },getAllPagesCmd (model.User),Unchanged
    | LoadedPageList pages -> 
        { model with Action = Listing (Loaded(pages)) },[],Unchanged
    | LoadedPage page ->
        let state = 
            { 
                Page = page; 
                IdentifierWhenLoaded = (Slug.get page.Location.Id); 
                WarnIdentifier = false; 
                Preview = None; 
                ShowingFilePicker = false; 
                Files = []
                SelectedFile = None
                LinkTarget = None
            }
        { model with Action = Editing (Loaded state) }, [], Unchanged
    | PreviewPage ->
        editingActionU model id (fun _ ps -> previewPageCmd model.User.Token ps.Page)
    | PagePreviewRendered preview ->
        editingActionU model (fun ps -> { ps with Preview = Some preview }) (fun _ _ -> [])
    | ClosePreview ->
        editingActionU model (fun ps -> { ps with Preview = None }) (fun _ _ -> [])
    | PageClicked (slug, action) ->
        match action with
        | Edit ->
            { model with Action = Editing (Loading slug) }, (getPageCmd (model.User) slug), Unchanged
        | Delete ->
            { model with Action = Editing (Loading slug); ShowConfirmDelete = true }, (getPageCmd (model.User) slug), Unchanged
    | FetchError exn ->
        model,[],Unchanged
    | ToggleLandingPage ->
        editingActionU model (fun ps -> { ps with Page = {ps.Page with IsLandingPage = not ps.Page.IsLandingPage}}) (fun _ _ -> [])